import React, { useContext } from 'react'
import { ClassNames } from '@emotion/react'

import { scaleImage } from '@news-mono/web-common'
import { LazyLoadingPicture, ImageSrcInfo } from './LazyLoadingPicture'
import {
    fixedRatioToBreakpointRatios,
    getCrops,
    useTrackIfPicureIsNotAvailable,
} from './utils'
import { pictureLayouts } from './layouts/layouts'
import { ResponsivePictureLayout } from './ResponsivePictureLayouts'
import {
    ImageSource,
    FixedRatio,
    BreakpointRatios,
} from '@news-mono/web-common'
import { ResponsivePictureSizes } from './responsive'
import { RenderTargetContext } from '@news-mono/web-common'

export interface ResponsivePictureProps {
    layout: ResponsivePictureLayout
    className?: string
    disableLazyLoad?: boolean
    image: ImageSource
    imageWidths: ResponsivePictureSizes
    fixedRatio: FixedRatio | BreakpointRatios
    /**
     * Allows the selection of the original crop if it matches the
     * requested fixed ratio
     **/
    allowOriginalCrop?: boolean
    renderPicture?: (pic: React.ReactElement) => React.ReactElement
}

export const WebPicture: React.FC<ResponsivePictureProps> = ({
    className,
    fixedRatio,
    image,
    imageWidths,
    renderPicture = (pic) => pic,
    layout,
    disableLazyLoad,
    allowOriginalCrop = false,
}) => {
    const scaledImage: ImageSrcInfo = {
        srcCallback: scaleImage,
        initialWidth: imageWidths.fallbackWidth,
    }

    const breakpointRatios = fixedRatioToBreakpointRatios(fixedRatio)
    const breakpointCrops = getCrops(
        image.crops,
        breakpointRatios,
        allowOriginalCrop,
    )
    const { renderTarget } = useContext(RenderTargetContext)

    const isUnavailable = !breakpointCrops
    useTrackIfPicureIsNotAvailable(isUnavailable, image)

    if (!breakpointCrops) {
        return null
    }

    const layoutPictureStyles = pictureLayouts[layout].pictureStyles()

    return renderPicture(
        <ClassNames>
            {({ css, cx }) => (
                <LazyLoadingPicture
                    pictureClassName={cx(css(layoutPictureStyles), className)}
                    fixedRatio={fixedRatio}
                    image={image}
                    imageSizes={imageWidths}
                    srcInfo={scaledImage}
                    disableLazyLoad={
                        disableLazyLoad || renderTarget === 'preview'
                    }
                    breakpointCrops={breakpointCrops}
                />
            )}
        </ClassNames>,
    )
}

WebPicture.displayName = 'WebPicture'
